import {
  BREAK_CATEGORIES,
  type NewSealedProduct,
  SEALED_FORMATS,
  type SealedFormat,
  type SealedProduct,
  type SealedStockUpdate,
} from "@hobbyshopify/backend-client";
import { useState } from "react";
import { useObserver, useSubtree } from "subtree.js/react";
import { LoadFailed, Loading } from "../../components/LoadState";
import { dollarsToCents, formatCents } from "../../core/primitives/live";
import { SEALED_FILTERS, SealedActions, SealedState } from "./sealed-model";

const FORMAT_LABELS: Record<SealedFormat, string> = {
  HOBBY_BOX: "Hobby box",
  BLASTER_BOX: "Blaster box",
  HANGER_BOX: "Hanger box",
  MEGA_BOX: "Mega box",
  CASE: "Case",
  TIN: "Tin",
  BOOSTER_BOX: "Booster box",
  OTHER: "Other",
};

export function sealedSubtitle(p: SealedProduct): string {
  return [p.brand, p.year ? String(p.year) : null, p.setName, FORMAT_LABELS[p.format]].filter(Boolean).join(" · ");
}

export function SealedPage() {
  const state = useSubtree(SealedState);
  const actions = useSubtree(SealedActions);
  const observer = useObserver();

  const filter = observer.watch(state.filter);
  const search = observer.watch(state.search);
  const status = observer.watch(state.status);
  const items = observer.watch(state.items);
  const busyId = observer.watch(state.busyId);
  const dialog = observer.watch(state.dialog);
  const dialogBusy = observer.watch(state.dialogBusy);
  const dialogError = observer.watch(state.dialogError);

  const loading = status.kind === "idle" || status.kind === "loading";

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="panel p-6">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-lg font-bold text-white">Sealed product</h1>
            <p className="text-sm text-muted">Boxes, cases and tins. What a live break opens comes from here.</p>
          </div>
          <button type="button" className="btn-primary btn-sm" onClick={actions.openAdd}>
            Add product
          </button>
        </div>

        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div className="flex gap-1">
            {SEALED_FILTERS.map((tab) => (
              <button
                type="button"
                key={tab.key}
                onClick={() => actions.setFilter(tab.key)}
                className={`rounded-btn px-3 py-1.5 text-xs font-medium transition-colors ${
                  filter === tab.key
                    ? "bg-accent text-ink"
                    : "bg-surface text-muted hover:bg-surface-strong hover:text-white"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <input
            type="search"
            placeholder="Search name, brand, set…"
            className="field max-w-xs"
            value={search}
            onChange={(e) => actions.setSearch(e.target.value)}
          />
        </div>

        {status.kind === "error" && (
          <LoadFailed title="Couldn't load your sealed product." message={status.message} onRetry={actions.retry} />
        )}

        {loading && items.length === 0 ? (
          <Loading label="Loading your sealed product…" />
        ) : status.kind === "error" && items.length === 0 ? null : items.length === 0 ? (
          <div className="py-10 text-center">
            <p className="mb-4 text-sm text-muted">
              {search || filter !== "all" ? "Nothing matches that." : "No sealed product yet."}
            </p>
            {!search && filter === "all" && (
              <button type="button" className="btn-primary btn-sm" onClick={actions.openAdd}>
                Add your first box
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {items.map((p) => (
              <SealedRow
                key={p.id}
                product={p}
                busy={busyId === p.id}
                onSetStock={() => actions.openStock(p)}
                onArchive={() => actions.openArchive(p)}
              />
            ))}
          </div>
        )}
      </div>

      {dialog?.kind === "add" && (
        <AddSealedModal
          busy={dialogBusy}
          error={dialogError}
          onSave={(p) => void actions.saveNew(p)}
          onCancel={actions.closeDialog}
        />
      )}
      {dialog?.kind === "stock" && (
        <SealedStockModal
          product={dialog.product}
          busy={dialogBusy}
          error={dialogError}
          onSave={(c) => void actions.saveStock(c)}
          onCancel={actions.closeDialog}
        />
      )}
      {dialog?.kind === "archive" && (
        <div className="scrim items-end justify-center p-4 sm:items-center">
          <div className="modal relative w-full max-w-sm p-6">
            <h2 className="mb-1 text-base font-semibold text-white">Archive this product?</h2>
            <p className="mb-4 text-sm text-muted">{dialog.product.name}</p>
            <p className="text-sm text-muted">
              It leaves the list and the show builder. Nothing already sold or broken changes.
            </p>
            {dialogError && <p className="note note-danger mt-3 text-danger">{dialogError}</p>}
            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={actions.closeDialog}
                disabled={dialogBusy}
                className="btn-secondary flex-1"
              >
                Keep
              </button>
              <button
                type="button"
                onClick={() => void actions.confirmArchive()}
                disabled={dialogBusy}
                className="btn-primary flex-1"
              >
                {dialogBusy ? "Archiving…" : "Archive"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SealedRow({
  product,
  busy,
  onSetStock,
  onArchive,
}: {
  product: SealedProduct;
  busy: boolean;
  onSetStock: () => void;
  onArchive: () => void;
}) {
  const subtitle = sealedSubtitle(product);
  return (
    <div className="flex gap-3 rounded-row border border-line bg-surface p-4">
      <div className="h-12 w-12 shrink-0 overflow-hidden rounded-thumb bg-surface-strong">
        {product.imageUrl ? (
          <img src={product.imageUrl} alt={product.name} className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-xs text-faint">Box</div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium text-white">{product.name}</div>
        {subtitle && <div className="truncate text-xs text-muted">{subtitle}</div>}
        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
          {product.status === "ARCHIVED" ? (
            <span className="badge badge-neutral">Archived</span>
          ) : product.available > 0 ? (
            <span className="badge badge-neutral">{product.available} available</span>
          ) : (
            <span className="badge badge-warn">Out of stock</span>
          )}
          {product.reserved > 0 && <span className="text-muted">{product.reserved} held by breaks</span>}
          {product.costCents != null && <span className="text-muted">cost {formatCents(product.costCents)}</span>}
          {product.marketValueCents != null && (
            <span className="text-muted">worth {formatCents(product.marketValueCents)}</span>
          )}
        </div>
      </div>
      {product.status === "ACTIVE" && (
        <div className="flex shrink-0 flex-col gap-1 sm:flex-row">
          <button type="button" className="btn-secondary btn-xs" disabled={busy} onClick={onSetStock}>
            Set stock
          </button>
          <button type="button" className="btn-ghost btn-xs" disabled={busy} onClick={onArchive}>
            Archive
          </button>
        </div>
      )}
    </div>
  );
}

function AddSealedModal({
  busy,
  error,
  onSave,
  onCancel,
}: {
  busy: boolean;
  error: string | null;
  onSave: (product: NewSealedProduct) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState("");
  const [brand, setBrand] = useState("");
  const [year, setYear] = useState("");
  const [set, setSet] = useState("");
  const [category, setCategory] = useState<SealedProduct["category"]>("CUSTOM");
  const [format, setFormat] = useState<SealedFormat>("HOBBY_BOX");
  const [quantity, setQuantity] = useState("1");
  const [cost, setCost] = useState("");
  const [marketValue, setMarketValue] = useState("");

  const qty = Number(quantity);
  const valid = name.trim() !== "" && Number.isInteger(qty) && qty >= 0;

  const submit = () => {
    const parsedYear = Number(year);
    onSave({
      name: name.trim(),
      brand: brand.trim() || undefined,
      year: Number.isInteger(parsedYear) && parsedYear > 0 ? parsedYear : undefined,
      setName: set.trim() || undefined,
      category,
      format,
      quantity: qty,
      costCents: cost.trim() ? dollarsToCents(cost) : undefined,
      marketValueCents: marketValue.trim() ? dollarsToCents(marketValue) : undefined,
    });
  };

  return (
    <div className="scrim items-end justify-center p-4 sm:items-center">
      <div className="modal relative w-full max-w-md p-6">
        <h2 className="mb-4 text-base font-semibold text-white">Add sealed product</h2>
        <div className="space-y-3">
          <label className="block">
            <span className="label">Name</span>
            <input
              className="field"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="2025 Prizm Basketball Hobby Box"
              maxLength={200}
            />
          </label>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="label">Brand</span>
              <input className="field" value={brand} onChange={(e) => setBrand(e.target.value)} placeholder="Panini" />
            </label>
            <label className="block">
              <span className="label">Year</span>
              <input
                className="field"
                inputMode="numeric"
                value={year}
                onChange={(e) => setYear(e.target.value)}
                placeholder="2025"
              />
            </label>
          </div>
          <label className="block">
            <span className="label">Set</span>
            <input className="field" value={set} onChange={(e) => setSet(e.target.value)} placeholder="Prizm" />
          </label>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="label">Sport or game</span>
              <select
                className="field"
                value={category}
                onChange={(e) => setCategory(e.target.value as SealedProduct["category"])}
              >
                {BREAK_CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c === "CUSTOM" ? "Other" : c}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="label">Format</span>
              <select className="field" value={format} onChange={(e) => setFormat(e.target.value as SealedFormat)}>
                {SEALED_FORMATS.map((f) => (
                  <option key={f} value={f}>
                    {FORMAT_LABELS[f]}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <label className="block">
              <span className="label">On hand</span>
              <input
                className="field"
                type="number"
                min={0}
                step={1}
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
              />
            </label>
            <label className="block">
              <span className="label">Cost each</span>
              <input
                className="field"
                inputMode="decimal"
                value={cost}
                onChange={(e) => setCost(e.target.value)}
                placeholder="$450"
              />
            </label>
            <label className="block">
              <span className="label">Worth each</span>
              <input
                className="field"
                inputMode="decimal"
                value={marketValue}
                onChange={(e) => setMarketValue(e.target.value)}
                placeholder="$600"
              />
            </label>
          </div>
          <p className="text-xs text-faint">Both private. The show builder uses them as its starting numbers.</p>
        </div>

        {error && <p className="note note-danger mt-3 text-danger">{error}</p>}

        <div className="mt-6 flex gap-3">
          <button type="button" onClick={onCancel} disabled={busy} className="btn-secondary flex-1">
            Cancel
          </button>
          <button type="button" onClick={submit} disabled={busy || !valid} className="btn-primary flex-1">
            {busy ? "Saving…" : "Add"}
          </button>
        </div>
      </div>
    </div>
  );
}

function SealedStockModal({
  product,
  busy,
  error,
  onSave,
  onCancel,
}: {
  product: SealedProduct;
  busy: boolean;
  error: string | null;
  onSave: (change: SealedStockUpdate) => void;
  onCancel: () => void;
}) {
  const [quantity, setQuantity] = useState(String(product.onHand));
  const [kind, setKind] = useState<"ADJUSTED" | "SHRINKAGE">("ADJUSTED");
  const [reason, setReason] = useState("");
  const [cost, setCost] = useState("");

  const parsed = Number(quantity);
  const valid = Number.isInteger(parsed) && parsed >= 0;
  const delta = valid ? parsed - product.onHand : 0;

  return (
    <div className="scrim items-end justify-center p-4 sm:items-center">
      <div className="modal relative w-full max-w-sm p-6">
        <h2 className="mb-1 text-base font-semibold text-white">Set stock level</h2>
        <p className="mb-4 truncate text-sm text-muted">{product.name}</p>

        <div className="space-y-3">
          <div>
            <div className="mb-1 block text-xs font-medium text-muted">On hand</div>
            <input
              type="number"
              min="0"
              step="1"
              inputMode="numeric"
              className={`field ${valid ? "" : "border-danger focus:border-danger"}`}
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
            />
            <p className="mt-1 text-xs text-faint">
              {valid
                ? delta === 0
                  ? `Currently ${product.onHand}. No change.`
                  : `Currently ${product.onHand} — this records ${delta > 0 ? `+${delta}` : delta}.`
                : "Enter a whole number, zero or more."}
              {product.reserved > 0 && ` ${product.reserved} held by live breaks.`}
            </p>
          </div>

          {delta < 0 && (
            <div>
              <div className="mb-1 block text-xs font-medium text-muted">Why fewer?</div>
              <select className="field" value={kind} onChange={(e) => setKind(e.target.value as typeof kind)}>
                <option value="ADJUSTED">We miscounted</option>
                <option value="SHRINKAGE">Lost, stolen or damaged</option>
              </select>
            </div>
          )}

          {delta > 0 && (
            <div>
              <div className="mb-1 block text-xs font-medium text-muted">Cost each (optional)</div>
              <input
                className="field"
                inputMode="decimal"
                value={cost}
                onChange={(e) => setCost(e.target.value)}
                placeholder="$450"
              />
            </div>
          )}

          <div>
            <div className="mb-1 block text-xs font-medium text-muted">Note</div>
            <input
              className="field"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={delta < 0 ? "Water damage, shelf recount…" : "Recount, restocked…"}
            />
          </div>
        </div>

        {error && <p className="note note-danger mt-3 text-danger">{error}</p>}

        <div className="mt-6 flex gap-3">
          <button type="button" onClick={onCancel} disabled={busy} className="btn-secondary flex-1">
            Cancel
          </button>
          <button
            type="button"
            onClick={() =>
              onSave({
                quantity: parsed,
                kind,
                reason: reason.trim() || undefined,
                costCents: delta > 0 && cost.trim() ? dollarsToCents(cost) : undefined,
              })
            }
            disabled={busy || !valid || delta === 0}
            className="btn-primary flex-1"
          >
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
